import { FunctionalComponent } from 'preact';
import { useCallback, useEffect } from 'preact/hooks';
import { Dialog, DialogFooter, DialogHeader, DialogManager } from '@accedo/ctv-ui-dialog';
import { Button as CtvButton } from '@accedo/ctv-ui-button';
import { DIALOG_TEXT } from '##/constants/text';
import { nav } from '##/services/navigation';

/** Delay (ms) between dialog dismiss and network re-check to allow the close animation to render */
const REFRESH_RECHECK_DELAY_MS = 0.5 * 1000;

/** Delay (ms) after mount before forcing focus to the Refresh button, giving child nav nodes time to register */
const FOCUS_ASSIGN_DELAY_MS = 100;

type NetworkErrorDialogProps = {
  onRefresh?: () => void;
};

export const NetworkErrorDialog: FunctionalComponent<NetworkErrorDialogProps> = ({ onRefresh }) => {
  const onClose = useCallback((): void => {
    DialogManager.close('cancel');
  }, []);

  const handleRefresh = useCallback((): void => {
    DialogManager.close('cancel');
    setTimeout(() => onRefresh?.(), REFRESH_RECHECK_DELAY_MS);
  }, [onRefresh]);

  useEffect(() => {
    const timer = setTimeout(() => {
      try {
        nav.assignFocus('refresh');
      } catch {
        // dialog may have already closed before the delay elapsed
      }
    }, FOCUS_ASSIGN_DELAY_MS);

    return () => clearTimeout(timer);
  }, []);

  return (
    <Dialog id={'ErrorDialog'} onOpenFocusId="refresh">
      <DialogHeader
        alignment="center"
        headerText={DIALOG_TEXT.NETWORK_CONNECTION_LOST}
        subHeaderText={DIALOG_TEXT.NETWORK_CHECK_CONNECTION}
      />
      <DialogFooter alignment="end">
        <CtvButton id="refresh" onSelect={handleRefresh} size="large" buttonType="secondary">
          {DIALOG_TEXT.REFRESH_BUTTON}
        </CtvButton>
        <CtvButton id="close" onSelect={onClose} size="large" buttonType="secondary">
          Close
        </CtvButton>
      </DialogFooter>
    </Dialog>
  );
};

export const NetworkErrorDialogElement = <NetworkErrorDialog />;
